import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type LucideIcon, Filter, X } from 'lucide-react';

import useDebounce from '../../hooks/useDebounce';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { usePageHeaderContext } from '../../context/PageHeaderContext';
import {
  hydrateModule,
  setModuleSort,
  type ListingSortState,
} from '../../utils/listingPreferencesSlice';
import { API_CONSTANTS } from '../../utils/constants';

import Dropdown from '../Dropdown';
import { IconButton } from './IconButton';
import { LISTING_MIN_HEIGHT } from './constants';
import { ListingLayout } from './ListingLayout';
import { ListingState } from './ListingState';
import { Pagination } from './Pagination';
import { SearchBar } from './SearchBar';
import { TableView } from './TableView';

import type { BreadcrumbConfig } from './PageHeader';
import type { ColDef } from 'ag-grid-community';
import type { AppFilter } from '../../model/appFilter';

type ViewMode = 'grid' | 'list' | 'table';

interface PrimaryAction {
  label: string;
  to?: string;
  onClick?: () => void;
  icon?: LucideIcon;
}

interface ExtraActionContext<TData> {
  refresh: () => void;
  reset: () => void;
  selectedIds: number[];
  page: number;
  limit: number;
  sort: { prop: string; dir: string }[];
  keyword: string;
  rows: TData[];
  isSelectionEnabled: boolean;
}

export interface FilterOption {
  value: string | number;
  label: string;
}

export interface FilterFieldConfig {
  key: string;
  label: string;
  type: 'select' | 'date' | 'text';
  options?: FilterOption[];
  placeholder?: string;
}

interface UniversalModuleViewProps<TData> {
  moduleKey: string;
  title: string;
  breadcrumbs?: BreadcrumbConfig[];
  apiEndpoint: string;
  columnsConfig: ColDef<TData>[];
  rowKey: (row: TData, index: number) => string | number;
  gridCardRenderer?: (row: TData) => React.ReactNode;
  listRowRenderer?: (row: TData) => React.ReactNode;
  tableRowRenderer?: (row: TData) => React.ReactNode[];
  selectable?: boolean;
  getRowId?: (row: TData, index: number) => number;
  bulkActions?: (selectedIds: number[]) => React.ReactNode;
  primaryAction?: PrimaryAction;
  extraActions?:
    | React.ReactNode
    | ((actions: ExtraActionContext<TData>) => React.ReactNode);
  searchPlaceholder?: string;
  pageSizes?: number[];
  initialPageSize?: number;
  listMethod?: 'POST' | 'GET';
  gridCardHeight?: number;
  listRowHeight?: number;
  refreshToken?: number;
  extraPayload?: Record<string, unknown>;
  defaultSort?: ListingSortState;
  filterConfig?: FilterFieldConfig[];
  note?: React.ReactNode;
  hideFilterButton?: boolean;
}

const getPersistedValue = <T,>(key: string, fallback: T): T => {
  if (typeof window === 'undefined') return fallback;
  const stored = localStorage.getItem(key);
  if (stored === null) return fallback;
  try {
    return JSON.parse(stored) as T;
  } catch {
    return stored as T;
  }
};

const resolveApiUrl = (endpoint: string) => {
  if (/^https?:\/\//i.test(endpoint)) return endpoint;
  const base = API_CONSTANTS.BASE_URL.replace(/\/+$/, '');
  const path = endpoint.replace(/^\/+/, '');
  return `${base}/${path}`;
};

const normalizeListResponse = <TData,>(payload: unknown) => {
  const typed = payload as {
    success?: boolean;
    message?: string;
    data?: any;
    meta?: {
      total?: number;
      itemCount?: number;
      page?: number;
      limit?: number;
    };
  };

  // Standard Format: data is the array, meta.total is the count
  const records = Array.isArray(typed.data) ? typed.data : [];

  const itemCount =
    typed.meta?.total ?? typed.meta?.itemCount ?? records.length ?? 0;

  return { records: records as TData[], itemCount };
};

const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];

export const UniversalModuleView = <TData,>({
  moduleKey,
  title,
  breadcrumbs,
  apiEndpoint,
  columnsConfig,
  rowKey,
  tableRowRenderer,
  selectable = false,
  getRowId,
  bulkActions,
  primaryAction,
  extraActions,
  searchPlaceholder = 'Search...',
  initialPageSize = 10,
  pageSizes = DEFAULT_PAGE_SIZES,
  listMethod = 'POST',
  refreshToken,
  extraPayload,
  defaultSort,
  filterConfig,
  note,
  hideFilterButton = false,
}: UniversalModuleViewProps<TData>) => {
  const dispatch = useAppDispatch();
  const { header, setTitle, setBreadcrumbs, setActions } = usePageHeaderContext();
  const listingPreferences = useAppSelector(
    (state) => state.listingPreferences?.modules?.[moduleKey]
  );
  const [rows, setRows] = useState<TData[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const pageSizeKey = `ui:${moduleKey}:pageSize`;
  const sortStorageKey = `ui:${moduleKey}:sort`;
  const storedPageSize = useMemo(
    () => getPersistedValue<number>(pageSizeKey, initialPageSize),
    [pageSizeKey, initialPageSize]
  );
  const storedSort = useMemo(
    () => getPersistedValue<ListingSortState | null>(sortStorageKey, null),
    [sortStorageKey]
  );
  const resolvedPageSize = pageSizes.includes(storedPageSize)
    ? storedPageSize
    : initialPageSize;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(resolvedPageSize);
  const initialSort =
    listingPreferences?.sort ??
    (storedSort?.direction ? storedSort : defaultSort);
  const [sortState, setSortState] = useState<ListingSortState>({
    field: initialSort?.field ?? '',
    direction: initialSort?.direction ?? null,
  });

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 400);

  const effectiveSearch = useMemo(() => {
    const trimmed = debouncedSearch.trim();
    return trimmed.length >= 3 || trimmed.length === 0 ? trimmed : '';
  }, [debouncedSearch]);

  const [activeFilters, setActiveFilters] = useState<AppFilter[]>([]);

  const [resetNonce, setResetNonce] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set());

  const [viewMode] = useState<ViewMode>('table');
  const [showFilters, setShowFilters] = useState(false);

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [listHeight, setListHeight] = useState<number>(0);
  const resolvedListHeight = listHeight || LISTING_MIN_HEIGHT;

  useEffect(() => {
    dispatch(
      hydrateModule({
        moduleKey,
        sort: storedSort,
      })
    );
  }, [dispatch, moduleKey, storedSort]);

  useEffect(() => {
    localStorage.setItem(pageSizeKey, JSON.stringify(limit));
  }, [limit, pageSizeKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(sortStorageKey, JSON.stringify(sortState));
    dispatch(setModuleSort({ moduleKey, sort: sortState }));
  }, [dispatch, moduleKey, sortState, sortStorageKey]);

  useEffect(() => {
    setPage(1);
  }, [effectiveSearch, activeFilters]);

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (!bodyRef.current) return;
      const nextHeight = bodyRef.current.clientHeight;
      setListHeight(nextHeight);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (bodyRef.current) {
      observer.observe(bodyRef.current);
    }
    window.addEventListener('resize', updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [viewMode, rows.length, showFilters]);

  useEffect(() => {
    setSelectedRowIds(new Set());
  }, [rows]);

  const resolvedSort = useMemo(
    () =>
      sortState.direction
        ? [{ prop: sortState.field, dir: sortState.direction }]
        : [],
    [sortState]
  );

  const payload = useMemo(() => {
    return {
      page,
      size: limit,
      sortBy: sortState.field || '',
      sortDirection: sortState.direction || '',
      search: effectiveSearch || '',
      ...extraPayload,
    };
  }, [
    page,
    limit,
    resolvedSort,
    effectiveSearch,
    searchValue,
    activeFilters,
    extraPayload,
  ]);

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const token = localStorage.getItem('access_token');
      const queryString =
        listMethod === 'GET'
          ? new URLSearchParams(
              Object.entries(payload).reduce<Record<string, string>>(
                (acc, [key, value]) => {
                  if (value === undefined || value === null || value === '') {
                    return acc;
                  }
                  if (typeof value === 'object') {
                    acc[key] = JSON.stringify(value);
                  } else {
                    acc[key] = String(value);
                  }
                  return acc;
                },
                {}
              )
            ).toString()
          : '';
      const url =
        listMethod === 'GET' && queryString
          ? `${resolveApiUrl(apiEndpoint)}?${queryString}`
          : resolveApiUrl(apiEndpoint);
      const response = await fetch(url, {
        method: listMethod,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: listMethod === 'POST' ? JSON.stringify(payload) : undefined,
      });
      const json = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error('Failed to load list');
      }
      const { records, itemCount } = normalizeListResponse<TData>(json);
      setRows(records);
      setTotalItems(itemCount);
    } catch (error) {
      setIsError(true);
      setRows([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [apiEndpoint, listMethod, payload]);

  const handleRefresh = useCallback(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh, refreshToken, resetNonce]);

  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  const onSortChange = (next: {
    field: string;
    direction: 'asc' | 'desc' | null;
  }) => {
    setSortState(next.direction ? next : { field: '', direction: null });
    setPage(1);
  };

  const renderBody = () => {
    const resolveRowId = (row: TData, index: number) =>
      getRowId?.(row, index) ?? index;
    const allIds = rows.map(resolveRowId);
    const isAllSelected =
      allIds.length > 0 && allIds.every((id) => selectedRowIds.has(id));
    const toggleSelectAll = () => {
      if (isAllSelected) {
        setSelectedRowIds(new Set());
      } else {
        setSelectedRowIds(new Set(allIds));
      }
    };
    const toggleRow = (row: TData, index: number) => {
      const rowId = resolveRowId(row, index);
      setSelectedRowIds((prev) => {
        const next = new Set(prev);
        if (next.has(rowId)) {
          next.delete(rowId);
        } else {
          next.add(rowId);
        }
        return next;
      });
    };

    const selectionColumn: ColDef<TData> = {
      headerName: '',
      field: '__select',
      width: 48,
      sortable: false,
      headerRenderer: () => (
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={toggleSelectAll}
          aria-label="Select all rows"
          className="w-4 h-4 rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
        />
      ),
      cellRenderer: ({ data }) => {
        if (!data) return null;
        const index = rows.indexOf(data as TData);
        const rowId = resolveRowId(data as TData, index);
        return (
          <input
            type="checkbox"
            checked={selectedRowIds.has(rowId)}
            onChange={() => toggleRow(data as TData, index)}
            aria-label="Select row"
            className="w-4 h-4 rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
          />
        );
      },
    };

    const tableColumns = selectable
      ? [selectionColumn, ...columnsConfig]
      : columnsConfig;

    if (isError && rows.length === 0) {
      return (
        <ListingState
          message="Unable to load records. Please try again."
          minHeight={resolvedListHeight}
          variant="error"
        />
      );
    }

    if (!isLoading && rows.length === 0) {
      return (
        <ListingState
          message="No Data Available"
          minHeight={resolvedListHeight}
        />
      );
    }

    return (
      <TableView
        rows={rows}
        columns={tableColumns}
        rowKey={rowKey}
        rowRenderer={tableRowRenderer}
        sortState={sortState}
        onSortChange={onSortChange}
        height={resolvedListHeight}
        minHeight={resolvedListHeight}
        isLoading={isLoading}
        isError={isError && rows.length === 0}
        emptyMessage="No Data Available"
      />
    );
  };

  const resetFiltersAndRefresh = useCallback(() => {
    setRows([]);
    setTotalItems(0);
    setIsError(false);
    setIsLoading(true);
    setSearchValue('');
    setSortState({ field: '', direction: null });
    setActiveFilters([]);
    setPage(1);
    setSelectedRowIds(new Set());
    setResetNonce((prev) => prev + 1);
  }, []);

  const handleFilterChange = (key: string, value: string | number) => {
    setActiveFilters((prev) => {
      const filtered = prev.filter((f) => f.key !== key);
      if (value === '' || value === 'ALL') return filtered;
      return [...filtered, { key, value }];
    });
  };

  const getFilterValue = (key: string) => {
    const filter = activeFilters.find((f) => f.key === key);
    return filter?.value ?? '';
  };

  const resolvedExtraActions = useMemo(() => {
    if (typeof extraActions !== 'function') {
      return extraActions;
    }
    return extraActions({
      refresh: handleRefresh,
      reset: resetFiltersAndRefresh,
      selectedIds: Array.from(selectedRowIds),
      page,
      limit,
      sort: resolvedSort,
      keyword: searchValue === '' ? '' : debouncedSearch,
      rows,
      isSelectionEnabled: selectable,
    });
  }, [
    debouncedSearch,
    extraActions,
    handleRefresh,
    limit,
    page,
    resolvedSort,
    resetFiltersAndRefresh,
    rows,
    searchValue,
    selectedRowIds,
    selectable,
  ]);

  const breadcrumbsKey = useMemo(() => {
    if (!breadcrumbs || breadcrumbs.length === 0) return '';
    return breadcrumbs
      .map(
        (item) =>
          `${item.label}|${item.to ?? ''}|${item.href ?? ''}|${
            item.current ? '1' : '0'
          }`
      )
      .join('>');
  }, [breadcrumbs]);

  const resolvedBreadcrumbs = useMemo(() => breadcrumbs, [breadcrumbsKey]);

  useEffect(() => {
    if (title) {
      setTitle(title);
    }
  }, [title, setTitle]);

  useEffect(() => {
    if (
      resolvedBreadcrumbs &&
      resolvedBreadcrumbs.length > 0 &&
      (header.breadcrumbs?.length ?? 0) <= 1
    ) {
      setBreadcrumbs(resolvedBreadcrumbs);
    }
  }, [resolvedBreadcrumbs, header.breadcrumbs?.length, setBreadcrumbs]);

  useEffect(() => {
    return () => {
      setTitle(undefined);
      setBreadcrumbs(undefined);
    };
  }, [setTitle, setBreadcrumbs]);

  const toolbarActions = useMemo(
    () => (
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
        {bulkActions && bulkActions(Array.from(selectedRowIds))}
        {resolvedExtraActions}
      </div>
    ),
    [bulkActions, resolvedExtraActions, selectedRowIds]
  );

  const clearFilters = () => {
    setActiveFilters([]);
  };

  const headerActions = useMemo(
    () => (
      <div className="flex flex-wrap items-center justify-end gap-2">
        <SearchBar
          value={searchValue}
          onChange={setSearchValue}
          onSearch={() => setPage(1)}
          placeholder={searchPlaceholder}
        />
        {activeFilters.length > 0 && (
          <button
            onClick={clearFilters}
            className="text-xs text-neutral-500 hover:text-error-500 flex items-center gap-1 px-2 py-1"
          >
            <X className="w-3 h-3" />
            Clear Filters
          </button>
        )}
        {!hideFilterButton && (
          <IconButton
            icon={Filter}
            title="Filter"
            onClick={() => setShowFilters((prev) => !prev)}
          >
            <span>{showFilters ? 'Hide Filter' : 'Filter'}</span>
          </IconButton>
        )}
        {(bulkActions || resolvedExtraActions) && (
          <div className="w-px h-6 bg-neutral-200 dark:bg-neutral-800 mx-1 hidden sm:block" />
        )}
        {toolbarActions}
      </div>
    ),
    [
      activeFilters.length,
      bulkActions,
      hideFilterButton,
      resolvedExtraActions,
      searchPlaceholder,
      searchValue,
      setSearchValue,
      showFilters,
      toolbarActions,
    ]
  );

  useEffect(() => {
    setActions(headerActions);
    return () => setActions(undefined);
  }, [headerActions, setActions]);

  return (
    <ListingLayout
      note={note}
      footer={
        <Pagination
          currentPage={page}
          itemsPerPage={limit}
          totalItems={totalItems}
          totalPages={totalPages}
          pageSizes={pageSizes}
          onPageChange={(nextPage) => setPage(nextPage)}
          onItemsPerPageChange={(nextSize) => {
            setLimit(nextSize);
            setPage(1);
          }}
        />
      }
    >
      <div className="bg-white dark:bg-neutral-950 border-neutral-200 mb-4 dark:border-neutral-800 space-y-4">
        {showFilters && filterConfig && filterConfig.length > 0 && (
          <div className="pt-2">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filterConfig.map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <label className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    {field.label}
                  </label>
                  {field.type === 'select' ? (
                    <Dropdown
                      value={getFilterValue(field.key)}
                      setValue={(val) =>
                        handleFilterChange(field.key, String(val))
                      }
                      options={[
                        { label: `All ${field.label}`, value: '' },
                        ...(field.options || []),
                      ]}
                      placeholder={`All ${field.label}`}
                      appendTo="body"
                    />
                  ) : field.type === 'date' ? (
                    <input
                      type="date"
                      value={getFilterValue(field.key)}
                      onChange={(e) =>
                        handleFilterChange(field.key, e.target.value)
                      }
                      className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  ) : (
                    <input
                      type="text"
                      placeholder={
                        field.placeholder ?? `Filter by ${field.label}...`
                      }
                      value={getFilterValue(field.key)}
                      onChange={(e) =>
                        handleFilterChange(field.key, e.target.value)
                      }
                      className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div ref={bodyRef} className="flex-1 min-h-0 max-w-full">
        {renderBody()}
      </div>
    </ListingLayout>
  );
};
